import React, { useEffect, useState } from "react"
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'
import DeleteModal from 'pages/parts/DeleteModal'
import Rightbar from 'pages/main/cheques/Rightbar'
import { returnSign, formatDate, formatMoney, findKeyFromArrayByValue, regexPhoneNumber } from "helpers/helpers";
import { DELETE, GET, httpOk } from 'helpers/api'

const TYPE_GENERAL = 7

const emptyFilter = {
	start_date: null,
	end_date: null,
	status: null,
	order_by: null,
	search: '',
}

// Цвет ячейки статуса кода: заливка всей ячейки, как во всех таблицах кабинета
const STATUS_CLASSES = {
	'used': 'bg-success text-white',
	'expired': 'bg-secondary text-white',
	'annulled': 'bg-danger text-white',
	'waiting': '',
}

function Tile({ title, value, sub, icon, color, active, onClick }) {
	return (
		<div className="card h-100 mb-0" role={onClick ? 'button' : undefined} onClick={onClick}
			style={{ 'cursor': onClick ? 'pointer' : 'default', 'outline': active ? `2px solid ${color}` : 'none' }}>
			<div className="card-body" style={{ 'padding': '12px' }}>
				<div className="d-flex justify-content-between align-items-start">
					<div className="text-muted fz-12" style={{ 'textTransform': 'uppercase', 'letterSpacing': '0.5px' }}>{title}</div>
					<i className={`uil ${icon}`} style={{ 'fontSize': '18px', 'color': color }}></i>
				</div>
				<div className="mt-1 fw-700" style={{ 'fontSize': '22px', 'whiteSpace': 'nowrap' }}>{value}</div>
				{sub &&
					<div className="text-muted fz-12">{sub}</div>
				}
			</div>
		</div>
	)
}

function Report() {
	const { t } = useTranslation()
	const { id } = useParams()

	const statuses = [
		{ 'id': 'waiting', 'name': t('promo_status_waiting') },
		{ 'id': 'used', 'name': t('promo_status_used') },
		{ 'id': 'expired', 'name': t('promo_status_expired') },
		{ 'id': 'annulled', 'name': t('promo_status_annulled') },
	]

	const orderBy = [
		{ 'id': 'id,desc', 'name': t('promo_order_newest') },
		{ 'id': 'id,asc', 'name': t('promo_order_oldest') },
		{ 'id': 'activated_at,desc', 'name': t('promo_order_last_used') },
		{ 'id': 'discount,desc', 'name': t('promo_order_biggest_discount') },
	]

	const [modalDelete, setModalDelete] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ id: 0, index: 0 })
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterData, setFilterData] = useState(emptyFilter)
	const [pagination, setPagination] = useState({
		url: '/discount/promo-codes-statuses',
		response: null
	})
	const [data, setData] = useState({})
	const [showRightBar, setShowRightBar] = useState(false)
	const [selectedCheque, setSelectedCheque] = useState({})

	const rule = data.rule ?? {}
	const summary = data.summary ?? {}
	const conversion = Number(summary.issued) > 0
		? Math.round(Number(summary.used) * 1000 / Number(summary.issued)) / 10
		: 0

	async function searchWithFilter(nextFilterData = null) {
		var filterDataCopy = { ...(nextFilterData || filterData) }
		var urlParams = ""

		if (filterDataCopy.status)
			urlParams += returnSign(urlParams) + 'status=' + filterDataCopy.status
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + encodeURIComponent(filterDataCopy.search)
		if (filterDataCopy.order_by)
			urlParams += returnSign(urlParams) + 'order_by=' + filterDataCopy.order_by
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		const filterUrl = `/discount/promo-codes-statuses/${id}${urlParams}`
		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}

		setDropdownIsOpen(false)
	}

	function applyFilter(patch) {
		const nextFilterData = { ...filterData, ...patch, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function resetFilter() {
		setFilterData(emptyFilter)
		searchWithFilter(emptyFilter)
	}

	function filterExists() {
		return !!(filterData.status || filterData.start_date || filterData.end_date || filterData.search || filterData.order_by)
	}

	async function paginate(page) {
		const nextFilterData = { ...filterData, 'page': page }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	async function openCheque(chequeId) {
		const response = await GET(`/cheques/${chequeId}`, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedCheque(response.data.data)
			setShowRightBar(true)
		}
	}

	async function deleteItem() {
		const response = await DELETE('/discount/promo-codes-statuses', { 'id': deleteModalParams.id }, { loader: true })
		if (httpOk(response)) {
			searchWithFilter()
			setModalDelete(false)
		}
	}

	function returnStatusText(item) {
		if (item.status_code === 'used') return formatDate(item.activated_at, 'dd.MM.yy HH:mm')
		return findKeyFromArrayByValue(statuses, 'id', item.status_code, 'name')
	}

	function returnDiscountText(item) {
		if (Number(item.discount_payment_type) === 2) return `${formatMoney(item.discount_payment_value)} %`
		return `${formatMoney(item.discount_payment_value)} ${t('currency_soum')}`
	}

	// Условия правила строкой — те же, что в списке промокодов
	function returnConditions(item) {
		const parts = []
		if (item.min_order_amount) parts.push(t('promo_cond_from', { 'value': formatMoney(item.min_order_amount) }))
		if (item.max_discount_amount) parts.push(t('promo_cond_max', { 'value': formatMoney(item.max_discount_amount) }))
		if (item.usage_limit) parts.push(t('promo_cond_limit', { 'used': formatMoney(item.total_used), 'limit': formatMoney(item.usage_limit) }))
		if (item.starts_at) parts.push(t('promo_cond_since', { 'date': formatDate(item.starts_at) }))
		if (item.ends_at) parts.push(t('promo_cond_until', { 'date': formatDate(item.ends_at) }))
		if (item.validity_days) parts.push(t('promo_cond_validity', { 'days': item.validity_days }))
		return parts.join(' · ')
	}

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex text-truncate">
				<h4 className="vertical-center h-100">
					<Link to="/discount/promo-codes">{t('promocodes')}</Link>
				</h4>
				<div className="vertical-center">
					<i className="uil uil-angle-double-right fz-20"></i>
				</div>
				<div className="vertical-center">
					<h6>{rule.name}</h6>
				</div>
			</div>

			{/* Шапка правила: что за код и на каких условиях */}
			{rule.id &&
				<div className="card mb-3">
					<div className="card-body d-flex flex-wrap gap-4">
						<div>
							<div className="text-muted fz-12">{t('type')}</div>
							<div className="fw-500">{t('promo_type_' + rule.discount_type_value, { 'defaultValue': rule.discount_type_name })}</div>
						</div>
						<div>
							<div className="text-muted fz-12">{t('promo_col_code')}</div>
							<div className="fw-700">
								{rule.discount_type_id === TYPE_GENERAL ? rule.promo_code : `${rule.prefix}…`}
							</div>
						</div>
						<div>
							<div className="text-muted fz-12">{t('discount')}</div>
							<div className="fw-700">{returnDiscountText(rule)}</div>
						</div>
						{returnConditions(rule) &&
							<div>
								<div className="text-muted fz-12">{t('promo_conditions')}</div>
								<div>{returnConditions(rule)}</div>
							</div>
						}
						<div>
							<div className="text-muted fz-12">{t('status')}</div>
							<div className={rule.status === 1 ? 'text-success fw-500' : 'text-danger fw-500'}>
								{rule.status === 1 ? t('promo_rule_active') : t('promo_rule_disabled')}
							</div>
						</div>
					</div>
				</div>
			}

			{/* Итоги за выбранный период; карточка статуса фильтрует таблицу */}
			<div className="row g-3 mb-3">
				<div className="col-xl-2 col-md-4 col-sm-6">
					<Tile title={t('promo_issued')} value={formatMoney(summary.issued)} icon="uil-ticket" color="#3b82f6"
						active={!filterData.status} onClick={() => applyFilter({ 'status': null })} />
				</div>
				<div className="col-xl-2 col-md-4 col-sm-6">
					<Tile title={t('promo_status_used')} value={formatMoney(summary.used)} icon="uil-check-circle" color="#16a34a"
						sub={`${t('promo_conversion')}: ${conversion}%`}
						active={filterData.status === 'used'} onClick={() => applyFilter({ 'status': 'used' })} />
				</div>
				<div className="col-xl-2 col-md-4 col-sm-6">
					<Tile title={t('promo_status_waiting')} value={formatMoney(summary.waiting)} icon="uil-clock" color="#f59e0b"
						active={filterData.status === 'waiting'} onClick={() => applyFilter({ 'status': 'waiting' })} />
				</div>
				<div className="col-xl-2 col-md-4 col-sm-6">
					<Tile title={t('promo_status_expired')} value={formatMoney(summary.expired)} icon="uil-hourglass" color="#6b7280"
						sub={Number(summary.annulled) > 0 ? `${t('promo_status_annulled')}: ${formatMoney(summary.annulled)}` : null}
						active={filterData.status === 'expired'} onClick={() => applyFilter({ 'status': 'expired' })} />
				</div>
				<div className="col-xl-2 col-md-4 col-sm-6">
					<Tile title={t('promo_discount_amount')} value={formatMoney(summary.discount_amount)} icon="uil-percentage" color="#ef4444"
						sub={t('currency_soum')} />
				</div>
				<div className="col-xl-2 col-md-4 col-sm-6">
					<Tile title={t('promo_revenue')} value={formatMoney(summary.revenue)} icon="uil-money-bill" color="#8b5cf6"
						sub={t('promo_revenue_hint')} />
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex flex-wrap gap-2 mb-3">
						<div>
							<FilterSearch
								value={filterData.search}
								onSearchChange={(search) => applyFilter({ search })}>
							</FilterSearch>
						</div>
						<div>
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('status')}</label>
											<Select
												isClearable
												options={statuses}
												value={statuses.find(option => option.id === filterData.status) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'status': option ? option.id : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										<div className="form-group col-md-6">
											<label>{t('order_by')}</label>
											<Select
												isClearable
												options={orderBy}
												value={orderBy.find(option => option.id === filterData.order_by) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'order_by': option ? option.id : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row">
										<div className="form-group col-md-12">
											<label>{t('promo_issue_period')}</label>
											<PeriodPicker
												withTime={false}
												clearable
												startDate={filterData.start_date}
												endDate={filterData.end_date}
												onChange={(period) => setFilterData({ ...filterData, ...period })}
											/>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex gap-2 justify-content-end">
												<button className="btn btn-secondary"
													onClick={() => setDropdownIsOpen(false)}>
													{t('cancel')}
												</button>
												<button className="btn btn-primary"
													onClick={() => applyFilter({})}>
													{t('apply')}
												</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</div>

					{filterExists() &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('promo_issue_period')}: </b>
										{formatDate(filterData.start_date)} | {formatDate(filterData.end_date)}
									</span>
								}
								{filterData.status &&
									<span className="me-2">
										<b>{t('status')}: </b>
										{findKeyFromArrayByValue(statuses, 'id', filterData.status, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-secondary text-truncate"
									onClick={resetFilter}>
									<i className="uil uil-times"></i>
									<span>{t('reset')}</span>
								</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '10%' }}>{t('promo_code')}</th>
									<th style={{ 'width': '18%' }}>{t('client')}</th>
									<th style={{ 'width': '12%' }}>{t('promo_issued_by')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('promo_issued_at')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('promo_valid_until')}</th>
									<th style={{ 'width': '12%' }} className="text-center">{t('status')}</th>
									<th style={{ 'width': '8%' }} className="text-center">{t('promo_cheque')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('promo_cheque_amount')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('discount')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={item.id}>
										<td className="text-center">{(Number(filterData.page) || 0) * 20 + index + 1}</td>
										<td className="fw-500">{item.promo_code}</td>
										<td>
											{item.client_name}
											<div className="text-muted fz-12">{regexPhoneNumber(item.client_phone)}</div>
										</td>
										<td>{item.created_by}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
										<td className="text-center">{item.expiration_date ? formatDate(item.expiration_date) : '—'}</td>
										<td className={`text-center ${STATUS_CLASSES[item.status_code] ?? ''}`}>
											{returnStatusText(item)}
										</td>
										<td className="text-center">
											{item.cheque_id ?
												<span className="text-primary cursor" role="button"
													onClick={() => openCheque(item.cheque_id)}>
													№{item.cheque_number ?? item.cheque_id}
												</span>
												:
												'—'
											}
										</td>
										<td className="text-end">{item.cheque_amount ? formatMoney(item.cheque_amount) : ''}</td>
										<td className="text-end">{item.discount ? formatMoney(item.discount) : ''}</td>
										<td>
											{/* Аннулировать можно только выданный и ещё не использованный код */}
											{item.status_code === 'waiting' &&
												<div className="table-dropdown">
													<Dropdown id={`dropdown-${index}`}>
														<Dropdown.Toggle variant="outline-primary">
															<i className="uil uil-ellipsis-v"></i>
														</Dropdown.Toggle>
														<Dropdown.Menu>
															<div className="table-dropdown-item text-danger"
																onClick={() => {
																	setDeleteModalParams({ id: item.id, index: index });
																	setModalDelete(true)
																}}>
																<i className="uil-times"></i>
																{t('promo_annul')}
															</div>
														</Dropdown.Menu>
													</Dropdown>
												</div>
											}
										</td>
									</tr>
								))}
								{data?.data?.length === 0 &&
									<tr>
										<td colSpan="11" className="text-center text-muted py-4">{t('promo_report_empty')}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}>
						</FilterPagination>
					}
				</div>
			</div>

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>

			<Rightbar showRightBar={showRightBar} setShowRightBar={setShowRightBar} selectedItem={selectedCheque} />
		</>
	)
}

export default Report
